// 单企业分析 → 真实信数 MCP 企业数据管道封装。
// 链路：POST /enterprise/enrich（需鉴权头）→ 返回 taskId → 轮询 /tasks/{id} 直至 success。

import {
  isNetworkError,
  throwApiError,
  toNetworkApiError,
} from '@/services/apiError';
import { authHeaders } from '@/services/authApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export interface TaxScanItem {
  tax_type: string;
  status: string;
  evidence_note?: string;
  trigger_signal?: string | null;
  candidate_action?: string | null;
  service_product?: string | null;
}

export interface EnterpriseLead {
  lead_id: string;
  lead_type: string;
  title: string;
  evidence_level?: string;
  maturity?: string;
  risk_score?: number | null; // H 风险严重度 0-100
  priority_score?: number | null; // C 商业优先级 0-100
  maturity_score?: number | null; // M 成熟度 0-100
  timeliness?: string; // 时效窗口原始串 window/deadline/remaining_days
  window?: string | null; // 即时/短期(≤90天)/中期(≤365天)/长期/U
  deadline?: string | null; // 截止日 YYYY-MM-DD
  remaining_days?: number | null; // 相对 as_of_date 剩余天数；已过期为负
  public_facts?: string;
  assumptions_to_verify?: string;
  data_gaps?: string[];
  service_products?: string[];
  tax_types?: string[];
  verification_action?: string;
}

export interface EnrichRaw {
  company_name: string;
  basic: Record<string, unknown>;
  risk: Array<Record<string, unknown>>;
  changes: Array<Record<string, unknown>>;
  qualification: Record<string, unknown>;
  tax_credit: Array<Record<string, unknown>>;
}

export interface EnterpriseInsight {
  data_quality?: Record<string, unknown>;
  company_profile?: Record<string, unknown>;
  business_portrait?: Record<string, unknown>;
  event_timeline?: unknown[];
  relationships?: unknown[];
  leads?: EnterpriseLead[];
  benefit_cards?: unknown[];
  priority_leads?: EnterpriseLead[];
  evidence_chains?: unknown[];
  interview_checklist?: unknown[];
  service_plans?: unknown[];
  policy_library?: unknown[];
  tax_scan?: TaxScanItem[];
  d_exclusions?: unknown[];
  search_log?: unknown[];
  quote_strategy?: unknown[];
  conclusion_boundary?: string;
}

export interface EnrichResultData {
  insight: EnterpriseInsight;
  report_markdown: string;
  raw: EnrichRaw;
  /** 后端任务 result 直出的组装结果（推理路径）；旧 Agent 路径可能为空 */
  company?: EnrichCompanyPayload;
  profile?: EnrichProfilePayload;
  leads?: V3LeadsPayload;
  leadsPreview?: LeadPreviewItem[];
  materialRequirements?: MaterialRequirementRow[];
  // 多轮 Agent 会话轨迹（可选）；用于前端「分析性能统计」展示。
  agent_trace?: AgentTraceInfo | null;
  // 各信数 MCP 源取数诊断（可选）；键为源名，值为 SourceDiagnosticInfo。
  fetch_diagnostics?: Record<string, SourceDiagnosticInfo> | null;
  // 用户上传资料包的逐文件扫描清单（可选）；含文件名/类别(用途)/档位/证据等级/大小/敏感标记。
  uploaded_files?: UploadedFileInfo[] | null;
}

/** 单个上传资料包文件的扫描信息（来自 document_scanner.scan_uploaded_files）。 */
export interface UploadedFileInfo {
  file_id: string;
  name: string;
  relative_path?: string;
  size_bytes: number;
  ext?: string;
  // 44 类资料清单分类：category=类别ID（如 TAX-02），category_name=用途名称（如 增值税申报）
  category?: string;
  category_name?: string;
  tier?: string;        // 数据包档位 P0/P1/P2/P3
  evidence?: string;    // 证据等级范围，如 E1-E2
  sensitive?: boolean;  // 是否敏感资料
  summary?: string;     // 文件内容摘要（截断）
  // ---- 取数诊断（供前端「分析性能统计」定位慢文件/未引用文件）----
  extract_ms?: number;      // 扫描阶段内容提取耗时（OCR 等重操作的主要来源）
  read_by_agent?: boolean;  // 模型是否主动 read_uploaded_file 读取过该文件全文
  read_count?: number;      // 被读取次数
  read_ms?: number;         // 按需读取总耗时
  read_chars?: number;      // 读取注入上下文的字符数
}

/** 单条工具调用轨迹。 */
export interface ToolCallInfo {
  tool_name: string;
  arguments_digest: string;
  duration_ms: number;
  ok: boolean;
  error?: string | null;
}

/** Agent 单轮轨迹：取数轮或产出轮。 */
export interface AgentRoundInfo {
  round_index: number;
  phase: 'tool' | 'writing';
  group?: string | null;
  tool_calls: ToolCallInfo[];
  output_chars: number;
  duration_ms: number;
  llm_ms: number;
  tool_ms: number;
  status: 'ok' | 'repaired' | 'degraded';
}

/** 整次 Agent 会话轨迹。 */
export interface AgentTraceInfo {
  rounds: AgentRoundInfo[];
  total_rounds: number;
  degraded_groups: string[];
  total_duration_ms: number;
  fetch_duration_ms: number;
  writing_duration_ms: number;
  converge_reason?: string | null;
  fallback_reason?: string | null;
}

/** 单个信数 MCP 源取数诊断。 */
export interface SourceDiagnosticInfo {
  has_data: boolean;
  duration_ms: number;
  bytes: number;
  from_cache: boolean;
  error?: string | null;
}

/** 后端 assemble 一次算好的展示槽位（白名单 §1.3） */
export type V3LeadDisplay = {
  trigger?: string;
  knownFacts?: string[];
  judgment?: string;
  eventDate?: string;
  toVerify?: string[];
  priorityLabel?: string;
  verifyStatus?: string;
  evidenceLevel?: string;
  judgmentReason?: string;
  publishDate?: string;
  nextActions?: string[];
  suggestedServices?: Array<{ category?: string; content?: string }>;
  evidenceSources?: Array<{
    name?: string;
    type?: string;
    date?: string;
    note?: string;
  }>;
};

/** Opportunity Service v3 /leads 单条（snake_case，与对端一致；对应底稿 O06） */
export type V3Lead = {
  lead_id?: string;
  id?: string;
  title?: string;
  lead_type?: string;
  maturity?: string;
  priority?: string;
  m_score?: number | null;
  p_score?: number | null;
  h_score?: number | null;
  period?: string;
  public_facts?: string[];
  inferences?: string[];
  unknowns?: string[];
  missing_evidence?: string[];
  tax_types?: string[];
  service_products?: string[];
  interview_questions?: Array<string | { q?: string }>;
  rule_ids?: string[];
  evidence_level?: string;
  markdown?: string | null;
  /** O06：atomic / group / detail / composite */
  origin_layer?: string;
  lead_cluster_id?: string;
  cluster_id?: string;
  group_id?: string;
  parent_lead_ids?: string[];
  lead_category?: string;
  /** 本侧组装层 display；有则前端优先直读，不再二次清洗 */
  display?: V3LeadDisplay;
  /** pool：来自潜在线索池并入 */
  source?: string;
  [key: string]: unknown;
};

export type ProfileMetrics = {
  enterpriseAgeYears?: number | null;
  registeredCapitalCny?: number | string | null;
  insuredCount?: number | null;
  industryClass?: string;
};

/** 对外主契约：透传 v3 leads。projected 时 active 已是展示列表，不再折叠。 */
export type V3LeadsPayload = {
  active: V3Lead[];
  excluded: V3Lead[];
  total: number;
  projected?: boolean;
  excludedCount?: number;
  /** 并入的潜在线索池有效条数 */
  poolCount?: number;
  source?: string;
};

export interface EnrichTask {
  id: string;
  status: string;
  progress: number;
  stage?: string | null;
  /** 任务入参快照：续轮询时用它还原企业名（不依赖 URL 上的 q） */
  payload?: { companyName?: string; [key: string]: unknown } | null;
  attempts?: number;
  max_attempts?: number;
  duration_ms?: number | null;
  error_message?: string | null;
  available_at?: string | null;
  result?: EnrichResultData | null;
  /** 推理路径：GET /tasks 顶层直出 */
  preview?: Record<string, unknown> | null;
  full?: Record<string, unknown> | null;
  leads?: V3LeadsPayload;
  leadsPreview?: Array<{
    id: string;
    leadType: string;
    title: string;
    estimatedValue?: string;
    summary?: string | null;
    maturityLevel?: string | null;
    sourceSection?: string | null;
  }>;
}

// 分阶段可见性：阶段标识 → 中文标签（必须与后端 _STAGE_ORDER / agent 上报的 stage 主阶段一一对应）
// 后端实际主阶段序列：fetching → scanning → drafting → extracting → reporting → done
// （queued/finalizing 仅为前端兼容占位，后端不会下发该 stage）
export const STAGE_LABELS: Record<string, string> = {
  queued: '排队中',
  fetching: '正在获取企业数据',
  scanning: '正在扫描资料与企业数据',
  drafting: '正在生成分析底稿',
  extracting: '正在提取企业洞察',
  reporting: '正在生成客户报告',
  finalizing: '正在整理结果',
  done: '已完成',
  // 推理路径 stage
  inferring: '正在生成全量分析',
  infer_full: '正在生成全量分析',
  infer_deep: '正在深度挖掘',
};

// Agent 取数轮收敛原因 → 中文标签
export const CONVERGE_LABELS: Record<string, string> = {
  model_converged: '模型自主收敛（已抓全所需源）',
  budget_exhausted: '超总预算强制收敛',
  max_rounds: '达最大轮数强制收敛',
  tool_unsupported: '不支持工具调用（已降级单次生成）',
};

/** 毫秒格式化为「Xs」/「X.Xs」/「Xm Ys」。 */
export function formatDuration(ms: number): string {
  if (!ms || ms < 0) return '0s';
  const totalSec = ms / 1000;
  if (totalSec < 60) {
    return totalSec < 10 ? `${totalSec.toFixed(1)}s` : `${Math.round(totalSec)}s`;
  }
  const m = Math.floor(totalSec / 60);
  const s = Math.round(totalSec % 60);
  return `${m}m${s}s`;
}

/** 字节数格式化为「X KB」/「X B」。 */
export function formatBytes(n: number): string {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  return `${(n / 1024).toFixed(1)} KB`;
}

/** 从后端 stage 列（格式 ``主阶段|细粒度detail``）解析出主阶段标识与文案。 */
export function parseStage(stage?: string | null): { main: string; label: string; detail?: string } | null {
  if (!stage) return null;
  const [main, detail] = stage.split('|', 2);
  return { main, label: STAGE_LABELS[main] ?? main, detail: detail || undefined };
}

export interface EnrichSubmitResult {
  taskId: string;
  status?: TaskStatus | string;
  /** 复用了既有任务（去重 / 单飞 / 幂等），未新建、未扣次 */
  deduped?: boolean;
  /** 单飞命中：当前用户已有运行中任务，本次被静默复用 */
  reusedRunningTask?: boolean;
  /** 单飞命中时**实际在跑**的企业名（可能不是本次提交的企业） */
  runningCompany?: string;
  fromCache?: boolean;
}

export async function enrichCompany(
  name: string,
  extraClues?: string,
  fileIds?: string[],
  opts?: { forceRerun?: boolean },
): Promise<EnrichSubmitResult> {
  const body: Record<string, unknown> = { companyName: name };
  if (extraClues != null && extraClues.trim()) body.extraClues = extraClues.trim();
  if (fileIds != null && fileIds.length > 0) body.fileIds = fileIds;
  // forceRerun：「重新分析」语义——跳过进行中复用、跳过已完成结果缓存，
  // 并先终止该用户正在跑的任务，确保真的重新走一遍分析链路。
  if (opts?.forceRerun) body.forceRerun = true;
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/enrich`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    // 用 ApiClientError 抛出（保留 status/code）：调用方需要区分 401（登录态失效，
    // 应重新登录并续接）与真正的分析失败，裸 Error 会把状态码丢掉。
    throwApiError(body, res.status, `提交失败 (${res.status})`);
  }
  const json = await res.json();
  return json.data as EnrichSubmitResult;
}

export interface UploadedFileMeta {
  fileId: string;
  name: string;
  size: number;
  type: string;
  /** 内容与企业目录中已有文件重复，已复用既有归档（未重复落盘） */
  deduped?: boolean;
}

export async function uploadFiles(
  files: File[],
  companyName: string,
): Promise<{ fileIds: string[]; files: UploadedFileMeta[] }> {
  const form = new FormData();
  for (const f of files) form.append('files', f);
  if (companyName.trim()) form.append('company_name', companyName.trim());
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message || `上传失败 (${res.status})`);
  }
  const json = await res.json();
  return json.data as { fileIds: string[]; files: UploadedFileMeta[] };
}

/** 企业归档目录中的历史资料条目（可跨任务复用）。 */
export interface HistoryFileMeta {
  fileId: string;
  name: string;
  relativePath?: string;
  size: number;
  type: string;
  uploadedAt?: string;
  category?: string | null;
  isArchive?: boolean;      // 是否为压缩包容器
  expandedCount?: number;   // 压缩包展开出的文件数
  legacy?: boolean;         // 是否来自早期 unknown 归档（修复企业名传参前上传）
}

/** 列出某企业已归档的全部历史资料（供管理与复用）。 */
export async function listHistoryFiles(
  companyName: string,
  includeLegacy = false,
): Promise<{ files: HistoryFileMeta[]; totalBytes: number }> {
  const q = new URLSearchParams({ company_name: companyName });
  if (includeLegacy) q.set('include_legacy', 'true');
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/uploads?${q}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('历史资料查询失败');
  const json = await res.json();
  return json.data as { files: HistoryFileMeta[]; totalBytes: number };
}

/** 删除某企业归档目录中的某个历史资料。 */
export async function deleteHistoryFile(
  fileId: string,
  companyName: string,
): Promise<{ removedFileIds: string[] }> {
  const q = new URLSearchParams({ company_name: companyName });
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/uploads/${fileId}?${q}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message || `删除失败 (${res.status})`);
  }
  const json = await res.json();
  return json.data as { removedFileIds: string[] };
}

export async function getEnrichTask(taskId: string): Promise<EnrichTask> {
  const res = await fetch(`${API_BASE_URL}/api/v1/tasks/${taskId}`, {
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throwApiError(body, res.status, `任务查询失败 (${res.status})`);
  }
  return (body as { data: EnrichTask }).data;
}

/** 任务状态（后端权威枚举，见 docs/contracts/api.md §3.1）。 */
export type TaskStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';

/** 运行中状态：占用「同一用户只允许一条」的唯一名额。 */
export const ACTIVE_TASK_STATUSES: TaskStatus[] = ['pending', 'processing'];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  pending: '排队中',
  processing: '分析中',
  completed: '已完成',
  failed: '分析失败',
  cancelled: '已取消',
};

export function isActiveTaskStatus(status?: string | null): boolean {
  return status === 'pending' || status === 'processing';
}

/**
 * 单飞命中：用户已有运行中任务，本次提交被**静默复用**到了另一家企业。
 *
 * 后端按需求返回 201 + 已有 taskId（不新建、不扣次）。前端不能静默接受——
 * 否则会把别家企业的结果当成这次提交的，故抛此错误交由页面提示并引导跳转。
 */
export class ReusedRunningTaskError extends Error {
  readonly taskId: string;
  readonly runningCompany: string;

  constructor(taskId: string, runningCompany: string) {
    super(
      runningCompany
        ? `已有任务正在处理中（${runningCompany}）`
        : '已有任务正在处理中',
    );
    this.name = 'ReusedRunningTaskError';
    this.taskId = taskId;
    this.runningCompany = runningCompany;
  }
}

export interface ActiveTaskInfo extends EnrichTask {
  /** 实际在分析的企业名（payload.companyName） */
  companyName: string;
  /** 存活判定窗口：超过该时长仍停留中间态即视为卡死，后端不再拦截新提交 */
  singleflightAliveSeconds: number;
}

/**
 * 当前用户唯一仍存活的运行中任务；没有则 `null`。
 *
 * 口径与提交侧单飞闸门完全一致，用于登录后提示与刷新页面恢复进度。
 * 网络异常时返回 `null`（提示是增强能力，不能因此阻断登录）。
 */
export async function getActiveTask(): Promise<ActiveTaskInfo | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/v1/tasks/active`, {
      headers: authHeaders(),
    });
    if (!res.ok) return null;
    const body = await res.json().catch(() => ({}));
    return (body as { data: ActiveTaskInfo | null }).data ?? null;
  } catch {
    return null;
  }
}

export async function cancelTask(taskId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/v1/tasks/${taskId}/cancel`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message || `取消任务失败 (${res.status})`);
  }
}

/**
 * 网络层失败（网关断流 / 连接被重置）自动重试。
 * 后端按「租户 + 企业 + 线索 + 文件」去重复用进行中任务，重试不会重复建任务。
 */
async function withNetworkRetry<T>(
  run: () => Promise<T>,
  signal?: AbortSignal,
  maxRetries = 2,
): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await run();
    } catch (err) {
      if (signal?.aborted || (err as Error)?.name === 'AbortError') throw err;
      if (!isNetworkError(err)) throw err;
      if (attempt >= maxRetries) {
        throw toNetworkApiError('网络连接中断，后台任务可能仍在继续，请稍后重试');
      }
      await new Promise((resolve) => setTimeout(resolve, 3000 * (attempt + 1)));
    }
  }
}

export async function enrichAndWait(
  name: string,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal,
  onStage?: (info: { progress: number; stage?: string | null; label?: string; detail?: string; durationMs?: number | null; attempts?: number; maxAttempts?: number; availableAt?: string | null }) => void,
  extraClues?: string,
  onTaskId?: (taskId: string) => void,
  fileIds?: string[],
  opts?: { forceRerun?: boolean },
): Promise<EnrichResultData> {
  // wait=false + 前端短轮询：每个请求都是秒级，避免长阻塞被反向代理切断
  const submitted = await withNetworkRetry(
    () => enrichCompany(name, extraClues, fileIds, opts),
    signal,
  );
  // 单飞命中且**企业不同**：后端把本次提交静默复用到了另一家在跑的任务上。
  // 此时若继续轮询，就会把「另一家企业的结果」渲染成本次提交的企业——
  // 必须中断并交给页面提示用户是否跳转到真正在跑的任务。
  if (
    submitted.reusedRunningTask &&
    (!submitted.runningCompany || submitted.runningCompany.trim() !== name.trim())
  ) {
    throw new ReusedRunningTaskError(
      submitted.taskId,
      submitted.runningCompany?.trim() || '另一家企业',
    );
  }
  const { taskId } = submitted;
  onTaskId?.(taskId);
  // 前端轮询上限需与后端 worker_task_timeout（4200s≈70min）对齐，避免任务仍在
  // 正常生成时前端提前误判超时。留约 10min 余量。
  const MAX_WAIT_MS = 60 * 60 * 1000;
  const POLL_INTERVAL_MS = 3000;
  const MAX_POLL_ERRORS = 5;
  const startedAt = Date.now();
  let pollErrors = 0;
  while (Date.now() - startedAt < MAX_WAIT_MS) {
    if (signal?.aborted) throw new DOMException('已取消', 'AbortError');
    let task: EnrichTask;
    try {
      task = await getEnrichTask(taskId);
      pollErrors = 0;
    } catch (err) {
      // 瞬时查询错误（网络抖动/网关 5xx）不应直接中断整段分析：退避后重试。
      pollErrors += 1;
      if (pollErrors > MAX_POLL_ERRORS) {
        throw new Error(
          `任务查询失败（已重试 ${MAX_POLL_ERRORS} 次）：${(err as Error)?.message || err}`,
        );
      }
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS * 2));
      continue;
    }
    if (task.status === 'completed') {
      if (!task.result) throw new Error('分析完成但未返回数据');
      return task.result;
    }
    if (task.status === 'failed' || task.status === 'cancelled') {
      throw new Error(task.error_message || '分析任务失败');
    }
    onProgress?.(task.progress);
    const parsed = parseStage(task.stage);
    onStage?.({ progress: task.progress, stage: task.stage, label: parsed?.label, detail: parsed?.detail, durationMs: task.duration_ms, attempts: task.attempts, maxAttempts: task.max_attempts, availableAt: task.available_at });
    // pending / processing：持续轮询；若处于退避窗口（available_at 在未来），按剩余时间等待。
    const backoffMs =
      task.available_at
        ? new Date(task.available_at).getTime() - Date.now()
        : 0;
    const waitMs =
      backoffMs > 0
        ? Math.min(Math.max(backoffMs, POLL_INTERVAL_MS), 10_000)
        : POLL_INTERVAL_MS;
    await new Promise((resolve) => setTimeout(resolve, waitMs));
  }
  throw new Error('分析任务超时（超过 15 分钟仍未完成），请稍后重试');
}

/** 一体化入口：先上传本地待提交文件，再提交分析任务。
 * 对前端而言是「一次点击」——用户选好资料后点查询，文件上传与分析提交合并执行。
 * files 为空数组时退化为普通 enrichAndWait。 */
export async function enrichWithFiles(
  name: string,
  extraClues: string | undefined,
  files: File[],
  onProgress?: (progress: number) => void,
  signal?: AbortSignal,
  onStage?: (info: { progress: number; stage?: string | null; label?: string; detail?: string; durationMs?: number | null; attempts?: number; maxAttempts?: number; availableAt?: string | null }) => void,
  onTaskId?: (taskId: string) => void,
  /** 该企业历史资料中勾选复用的 file_id（与本次上传的合并后一并提交） */
  reuseFileIds?: string[],
  /** 上传完成后回调：传出上传结果（含 deduped 标记），用于前端提示「已复用相同文件」 */
  onUploadResult?: (res: { fileIds: string[]; files: UploadedFileMeta[] }) => void,
  /** 「重新分析」：跳过进行中复用与结果缓存，真正重跑一遍分析链路 */
  opts?: { forceRerun?: boolean },
): Promise<EnrichResultData> {
  let fileIds: string[] | undefined;
  if (files.length > 0) {
    const up = await uploadFiles(files, name);
    fileIds = up.fileIds;
    onUploadResult?.(up);
  }
  // 合并历史复用资料：本次上传 + 用户勾选的历史文件（去重保序）
  if (reuseFileIds && reuseFileIds.length > 0) {
    const merged = [...(fileIds ?? [])];
    for (const id of reuseFileIds) {
      if (!merged.includes(id)) merged.push(id);
    }
    fileIds = merged;
  }
  return enrichAndWait(
    name,
    onProgress,
    signal,
    onStage,
    extraClues,
    onTaskId,
    fileIds,
    opts,
  );
}

// ---- 任务结果展示类型 ----

export type LeadPreviewItem = {
  id: string;
  leadType: string;
  title: string;
  estimatedValue: string;
  summary?: string | null;
  maturityLevel?: 'A' | 'B' | 'C' | 'D' | null;
  sourceSection?: string | null;
  /** 详情扩展（深挖透传） */
  publicFacts?: string | null;
  assumptionsToVerify?: string | null;
  dataGaps?: string[];
  serviceProducts?: string[];
  taxTypes?: string[];
  verificationAction?: string | null;
  evidenceLevel?: string | null;
  riskScore?: number | null;
  priorityScore?: number | null;
  maturityScore?: number | null;
  sourceIds?: string[];
  policyIds?: string[];
  serviceModule?: string | null;
  window?: string | null;
  deadline?: string | null;
  remainingDays?: number | null;
  policyStatus?: string | null;
  humanReview?: boolean | null;
  rankReason?: string | null;
  nextAction?: string | null;
  blockingItems?: string | null;
  serviceGapConfirmed?: string | null;
  /** 对齐 demo 八板块的详情；有则详情页可用 */
  detail?: import('@/mocks/coreLeadDetails/types').CoreLeadDetail | null;
  /** 线索相关底稿 Markdown，详情页优先渲染 */
  markdown?: string | null;
};

/** GET /enterprise/search 主体候选项（初步线索闭环） */
export type EnterpriseSearchItem = {
  companyId: string;
  name: string;
  shortName?: string;
  creditCode?: string;
  region?: string;
  industry?: string;
};

export type EnterpriseSearchResult = {
  query: string;
  items: EnterpriseSearchItem[];
};

/** 信数 raw.basic：统一代码常见 UNISCID / TAXID，不一定有 creditCode */
export function creditCodeFromRaw(raw: unknown): string {
  if (!raw || typeof raw !== 'object') return '';
  const root = raw as Record<string, unknown>;
  const basic = root.basic;
  const basicObj =
    basic && typeof basic === 'object' ? (basic as Record<string, unknown>) : {};
  const data =
    basicObj.data && typeof basicObj.data === 'object'
      ? (basicObj.data as Record<string, unknown>)
      : basicObj;
  const nested =
    data.BASIC && typeof data.BASIC === 'object'
      ? (data.BASIC as Record<string, unknown>)
      : data;
  for (const key of [
    'CREDITCODE',
    'creditCode',
    'UNISCID',
    'uniscid',
    'USCC',
    'uscc',
    'TAXID',
    'taxid',
    'tyshxydm',
  ]) {
    const val = nested[key];
    if (typeof val === 'string' && val.trim()) return val.trim();
  }
  return '';
}

export function withCreditCodeFromRaw(
  company: EnrichCompanyPayload | undefined,
  raw: unknown,
): EnrichCompanyPayload | undefined {
  if (!company) return company;
  if ((company.creditCode || '').trim()) return company;
  const code = creditCodeFromRaw(raw);
  return code ? { ...company, creditCode: code } : company;
}

export type EnrichCompanyPayload = {
  companyId?: string;
  name?: string;
  shortName?: string;
  creditCode?: string;
  region?: string;
  industry?: string;
  /** 成立日期 YYYY-MM-DD 等，来自 raw.basic */
  establishDate?: string;
  /** 人员/经营规模文案，如「100-499人」 */
  staffSize?: string;
};

/** 模型 /profile.sixLayer；结果页「主要经营特征」用 businessFeature */
export type ProfileSixLayer = {
  industry?: string;
  industryConfidence?: number;
  businessFeature?: string;
  chainRole?: string;
  businessModel?: string;
  customerMix?: string;
  productDelivery?: string;
  assetLabor?: string;
  notes?: string;
};

export type EnrichProfilePayload = {
  companyIntro?: string;
  characteristics?: string[];
  events?: Array<{ date?: string; title?: string; desc?: string }>;
  dataGaps?: string[];
  nextSteps?: string[];
  analysisNote?: string;
  /** m03：画像来自模型 /profile，空字段不另补写 */
  source?: string;
  metrics?: ProfileMetrics;
  sixLayer?: ProfileSixLayer | null;
  isFallback?: boolean;
  qualityGate?: unknown;
  /** @deprecated 勿直接渲染；用 dataGaps / blockingIssueMessages */
  blockingIssues?: unknown[];
  blockingIssueMessages?: string[];
};

export type MaterialRequirementRow = {
  name: string;
  usage: string;
  period: string;
  missingImpact: string;
  supportedLeadIds: string[];
  supportedLeadNames: string[];
};

export type EnrichPreviewData = {
  taskId: string;
  status: string;
  stage?: string;
  company?: EnrichCompanyPayload;
  profile?: EnrichProfilePayload;
  preview?: Record<string, unknown>;
  /** 主契约：v3 /leads（含本侧 display） */
  leads: V3LeadsPayload;
  /** 兼容旧路径；新 UI 优先 leads.active */
  leadsPreview: LeadPreviewItem[];
  /** 企业资料需求合并表（assemble） */
  materialRequirements?: MaterialRequirementRow[];
  /** 取数 raw（Mock / MCP），用于浅查询税务画像 */
  raw?: Record<string, unknown>;
  deduped?: boolean;
  /** 命中服务端缓存 / 进行中去重，不计初步权益次数 */
  fromCache?: boolean;
  quota?: {
    preliminaryLimit: number;
    preliminaryUsed: number;
    preliminaryRemaining: number;
  };
};

/** GET /enterprise/search — 模糊匹配（游客可调）。 */
export async function searchCompanies(
  q: string,
  limit = 10,
  signal?: AbortSignal,
): Promise<EnterpriseSearchResult> {
  const query = q.trim();
  if (query.length < 2) {
    throw new Error('请输入至少 2 个字符');
  }
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/search?${params}`, {
    method: 'GET',
    headers: { ...authHeaders() },
    signal,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    // 保留 status/code：调用方要能区分「未登录 401」与「额度用尽 403」并给不同引导
    throwApiError(json, res.status, `企业搜索失败 (${res.status})`);
  }
  const data = (json as { data?: EnterpriseSearchResult }).data;
  return {
    query: data?.query || query,
    items: Array.isArray(data?.items) ? data!.items : [],
  };
}

function apiErrorMessage(body: unknown, fallback: string, status?: number): string {
  const b = body as {
    error?: { code?: string; message?: string; retryable?: boolean };
    code?: string;
    message?: string;
    detail?: string | { message?: string; code?: string };
  } | null;
  if (!b || typeof b !== 'object') {
    if (status === 408) return '等待初步分析结果超时，请稍后重试';
    if (status === 503) return '大模型服务暂时不可用或已掉线，请稍后重试';
    return fallback;
  }
  const code =
    b.error?.code || b.code || (typeof b.detail === 'object' ? b.detail?.code : undefined);
  if (code === 'INFERENCE_OFFLINE' || status === 503) {
    return b.error?.message || b.message || '大模型服务暂时不可用或已掉线，请稍后重试';
  }
  if (code === 'ENRICH_TIMEOUT' || code === 'DEEP_TIMEOUT' || code === 'INFERENCE_TIMEOUT' || status === 408) {
    return (
      b.error?.message ||
      b.message ||
      (code === 'DEEP_TIMEOUT'
        ? '等待深度挖掘结果超时，请稍后重试'
        : code === 'INFERENCE_TIMEOUT'
          ? '大模型已连通，但未在时限内返回结果，请稍后重试'
          : '等待初步分析结果超时，请稍后重试')
    );
  }
  if (b.error?.message) return b.error.message;
  if (typeof b.message === 'string' && b.message && b.message !== 'ok') return b.message;
  if (typeof b.detail === 'string') return b.detail;
  if (b.detail && typeof b.detail === 'object' && b.detail.message) return b.detail.message;
  if (status === 408) return '等待初步分析结果超时，请稍后重试';
  return fallback;
}

/** 从错误文案推断展示类型（超时 / 掉线 / 无数据）。 */
export function classifyClientError(message: string): 'timeout' | 'offline' | 'generic' {
  const m = message || '';
  if (/超时|ENRICH_TIMEOUT|DEEP_TIMEOUT|INFERENCE_TIMEOUT|408/.test(m)) return 'timeout';
  if (/掉线|不可用|INFERENCE_OFFLINE|503/.test(m)) return 'offline';
  return 'generic';
}


/** 用任务上的 report_markdown 导出 PDF（模型底稿 + 可选的潜在线索池补充）。 */
export async function downloadEnrichReport(taskId: string, filename: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/v1/reports/export-pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ report_id: taskId, title: filename }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(apiErrorMessage(json, `报告下载失败 (${res.status})`, res.status));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** 将 leads / leadsPreview 填进最小 EnrichResultData，供现有 realResultToClient / 统计区兼容。 */
export function previewDataToEnrichResult(
  companyName: string,
  data: EnrichPreviewData,
): EnrichResultData {
  const fromV3 = (data.leads?.active || []).map((item) => ({
    lead_id: String(item.lead_id || item.id || ''),
    lead_type: String(item.lead_type || '其他'),
    title: String(item.title || '未命名线索'),
    maturity: item.maturity ?? undefined,
    public_facts: Array.isArray(item.public_facts)
      ? item.public_facts.join('；')
      : undefined,
    service_products: item.service_products,
    tax_types: item.tax_types,
    data_gaps: item.missing_evidence,
  }));
  const fromPreview: EnterpriseLead[] = (data.leadsPreview || []).map((item) => ({
    lead_id: item.id,
    lead_type: item.leadType,
    title: item.title,
    maturity: item.maturityLevel ?? undefined,
    public_facts: item.summary ?? undefined,
    service_products: item.estimatedValue ? [item.estimatedValue] : undefined,
  }));
  const leads = fromV3.length > 0 ? fromV3 : fromPreview;
  const basicFromCompany: Record<string, unknown> = {};
  if (data.company?.creditCode) basicFromCompany.creditCode = data.company.creditCode;
  if (data.company?.industry) basicFromCompany.industry = data.company.industry;
  if (data.company?.region) basicFromCompany.region = data.company.region;
  if (data.company?.establishDate) basicFromCompany.establishDate = data.company.establishDate;
  if (data.company?.staffSize) basicFromCompany.staffSize = data.company.staffSize;
  return {
    insight: {
      company_profile: { name: companyName, ...(data.company || {}) },
      leads,
      priority_leads: leads,
    },
    report_markdown: '',
    raw: {
      company_name: companyName,
      basic: {
        ...basicFromCompany,
        ...((data.raw?.basic as Record<string, unknown>) || {}),
      },
      risk: Array.isArray(data.raw?.risk)
        ? (data.raw.risk as Record<string, unknown>[])
        : [],
      changes: Array.isArray(data.raw?.changes)
        ? (data.raw.changes as Record<string, unknown>[])
        : [],
      qualification: (data.raw?.qualification as Record<string, unknown>) || {},
      tax_credit: Array.isArray(data.raw?.tax_credit)
        ? (data.raw.tax_credit as Record<string, unknown>[])
        : [],
    },
  };
}
